<template>
  <div class="fm-root" @keydown="onKeyDown" tabindex="0">

    <!-- ── Top bar ─────────────────────────────────── -->
    <div class="fm-topbar">
      <div class="fm-nav">
        <button class="fm-icon-btn" :disabled="!canGoBack" @click="goBack" title="Back (Backspace)">
          <i class="bx bx-chevron-left"></i>
        </button>
        <button class="fm-icon-btn" :disabled="!canGoUp" @click="goUp" title="Go up">
          <i class="bx bx-chevron-up"></i>
        </button>
      </div>

      <div class="fm-breadcrumb">
        <span class="fm-bc-item" @click="navigateTo(null)">
          <i class="bx bx-home-alt"></i>
        </span>
        <template v-for="(seg, i) in currentPath" :key="seg.id">
          <i class="bx bx-chevron-right fm-bc-sep"></i>
          <span class="fm-bc-item fm-bc-label" @click="navigateTo(seg.id)">{{ seg.label }}</span>
        </template>
      </div>

      <div class="fm-topbar-right">
        <div class="fm-search-wrap">
          <i class="bx bx-search"></i>
          <input v-model="searchQuery" class="fm-search-input" placeholder="Search…" />
        </div>
        <div class="fm-view-toggle">
          <button class="fm-icon-btn" :class="{ 'fm-icon-btn--active': view === 'grid' }" @click="view = 'grid'" title="Grid">
            <i class="bx bx-grid-alt"></i>
          </button>
          <button class="fm-icon-btn" :class="{ 'fm-icon-btn--active': view === 'list' }" @click="view = 'list'" title="List">
            <i class="bx bx-list-ul"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Body: sidebar + content ────────────────── -->
    <div class="fm-body">

      <!-- Folder tree sidebar -->
      <div class="fm-sidebar" :style="{ width: sidebarW + 'px' }">
        <FolderTree
          :allFiles="files"
          :currentId="currentFolderId"
          @navigate="navigateTo"
        />
      </div>

      <!-- Drag-resize handle -->
      <div class="fm-resize-handle" @mousedown.prevent="startResize"></div>

      <!-- Main content panel -->
      <div class="fm-content">

        <!-- Action strip -->
        <div class="fm-actionstrip">
          <button class="fm-strip-btn" @click="showNewFolderDialog = true" title="New folder">
            <i class="bx bx-folder-plus"></i><span>New folder</span>
          </button>
          <button class="fm-strip-btn" @click="triggerUpload" title="Upload">
            <i class="bx bx-upload"></i><span>Upload</span>
          </button>
          <div class="fm-strip-sep"></div>
          <button class="fm-strip-btn" :disabled="!selectedIds.size" @click="cutSelection" title="Cut">
            <i class="bx bx-cut"></i>
          </button>
          <button class="fm-strip-btn" :disabled="!selectedIds.size" @click="copySelection" title="Copy">
            <i class="bx bx-copy"></i>
          </button>
          <button class="fm-strip-btn" :disabled="!clipboard.length" @click="pasteClipboard" title="Paste">
            <i class="bx bx-paste"></i>
          </button>
          <button class="fm-strip-btn" :disabled="selectedIds.size !== 1" @click="renameSelected" title="Rename (F2)">
            <i class="bx bx-rename"></i>
          </button>
          <button class="fm-strip-btn fm-strip-btn--danger" :disabled="!selectedIds.size" @click="confirmDelete" title="Delete (Del)">
            <i class="bx bx-trash"></i>
          </button>
          <div class="fm-strip-sep"></div>
          <Select
            v-model="sortKey"
            :options="sortOptions"
            optionLabel="label"
            optionValue="value"
            class="fm-sort-sel"
            placeholder="Sort by"
          />
          <button class="fm-strip-btn fm-strip-btn--icon" @click="$emit('refresh', currentFolderId)" title="Refresh">
            <i class="bx bx-refresh"></i>
          </button>
        </div>

        <!-- File grid -->
        <FileGrid
          ref="grid"
          :items="currentItems"
          :view="view"
          :selectedIds="selectedIds"
          :searchQuery="searchQuery"
          :sortBy="sortBy"
          :sortDir="sortDir"
          @select="selectOne"
          @multi-select="toggleOne"
          @range-select="rangeSelect"
          @open="openItem"
          @context-menu="showContextMenu"
          @rename="e => $emit('rename', e)"
          @move="e => $emit('move', e)"
          @upload-files="uploadFiles"
          @clear-selection="selectedIds = new Set()"
          @sort="s => { sortBy = s.by; sortDir = s.dir }"
        />
      </div>
    </div>

    <!-- ── Status bar ──────────────────────────────── -->
    <div class="fm-statusbar">
      <span class="fm-status-text">{{ statusText }}</span>
      <div class="fm-storage-inline" v-if="storage">
        <div class="fm-storage-track">
          <div class="fm-storage-fill"
            :style="{ width: storagePercent + '%', background: storageColor }">
          </div>
        </div>
        <span class="fm-storage-label" :class="{ 'is-warn': storagePercent > 85 }">
          {{ formatSize(storage.used) }} / {{ formatSize(storage.total) }}
        </span>
      </div>
    </div>

    <!-- Helpers -->
    <input ref="uploadInput" type="file" multiple style="display:none" @change="onUploadInput" />
    <ContextMenu ref="ctxMenu" :model="contextMenuItems" />

    <Dialog v-model:visible="showNewFolderDialog" header="New Folder" :modal="true" :draggable="false" style="width:320px">
      <div style="padding: 8px 0 4px">
        <InputText
          v-model="newFolderName"
          placeholder="Folder name"
          autofocus
          style="width:100%"
          @keydown.enter="createFolder"
          @keydown.esc="showNewFolderDialog = false"
        />
      </div>
      <template #footer>
        <Button label="Cancel" text severity="secondary" @click="showNewFolderDialog = false" />
        <Button label="Create" @click="createFolder" :disabled="!newFolderName.trim()" />
      </template>
    </Dialog>

    <ConfirmDialog />
    <Toast position="bottom-right" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import ContextMenu from 'primevue/contextmenu';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Toast from 'primevue/toast';

import Select from 'primevue/select';
import FolderTree from './FolderTree.vue';
import FileGrid from './FileGrid.vue';

const props = defineProps({
  files: { type: Array, default: () => [] },
  rootId: { type: String, default: null },
  storage: { type: Object, default: null },
});

const emit = defineEmits(['open-file','new-folder','rename','delete','move','copy-paste','download','upload','refresh','navigate']);

const confirm = useConfirm();
const toast = useToast();
const grid = ref(null);
const ctxMenu = ref(null);
const uploadInput = ref(null);

const view = ref('grid');
const searchQuery = ref('');
const selectedIds = ref(new Set());
const currentFolderId = ref(props.rootId);
const history = ref([]);
const showNewFolderDialog = ref(false);
const newFolderName = ref('');
const clipboard = ref([]);
const contextMenuItems = ref([]);

// Sidebar resize
const sidebarW = ref(200);
let resizeStartX = 0, resizeStartW = 0;
function startResize(e) {
  resizeStartX = e.clientX;
  resizeStartW = sidebarW.value;
  const onMove = (ev) => { sidebarW.value = Math.max(140, Math.min(360, resizeStartW + ev.clientX - resizeStartX)); };
  const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
}

// Sort
const sortKey = ref('name-asc');
const sortOptions = [
  { label: 'Name A→Z',      value: 'name-asc' },
  { label: 'Name Z→A',      value: 'name-desc' },
  { label: 'Largest first', value: 'size-desc' },
  { label: 'Smallest first',value: 'size-asc' },
  { label: 'Newest first',  value: 'date-desc' },
  { label: 'Oldest first',  value: 'date-asc' },
];
const sortBy = computed(() => sortKey.value.split('-')[0] === 'date' ? 'modifiedAt' : sortKey.value.split('-')[0]);
const sortDir = computed(() => sortKey.value.split('-')[1]);

// Derived
const currentItems = computed(() => props.files.filter(f => (f.parentId ?? null) === currentFolderId.value));
const selectedItems = computed(() => props.files.filter(f => selectedIds.value.has(f.id)));
const currentPath = computed(() => {
  const path = []; let id = currentFolderId.value;
  while (id != null) {
    const f = props.files.find(x => x.id === id); if (!f) break;
    path.unshift({ id: f.id, label: f.name }); id = f.parentId ?? null;
  }
  return path;
});
const canGoBack = computed(() => history.value.length > 0);
const canGoUp = computed(() => currentFolderId.value != null);

const statusText = computed(() => {
  const total = currentItems.value.length;
  const sel = selectedIds.value.size;
  if (!sel) return `${total} item${total !== 1 ? 's' : ''}`;
  const size = selectedItems.value.filter(f => f.type === 'file').reduce((s, f) => s + (f.size || 0), 0);
  return `${sel} selected${size ? ' · ' + formatSize(size) : ''}`;
});

const storagePercent = computed(() => !props.storage ? 0 :
  Math.min(100, Math.round((props.storage.used / props.storage.total) * 100)));
const storageColor = computed(() =>
  storagePercent.value > 90 ? 'var(--danger-color)' : storagePercent.value > 75 ? 'var(--warning-color)' : 'var(--fm-accent)');

// Navigation
function openItem(item) {
  if (item.type === 'folder') { history.value.push(currentFolderId.value); currentFolderId.value = item.id; selectedIds.value = new Set(); emit('navigate', item.id); }
  else emit('open-file', item);
}
function goBack() { if (!canGoBack.value) return; currentFolderId.value = history.value.pop(); selectedIds.value = new Set(); }
function goUp() { const f = props.files.find(x => x.id === currentFolderId.value); if (!f) return; history.value.push(currentFolderId.value); currentFolderId.value = f.parentId ?? null; selectedIds.value = new Set(); }
function navigateTo(id) { history.value.push(currentFolderId.value); currentFolderId.value = id; selectedIds.value = new Set(); }

// Selection
function selectOne(item) { selectedIds.value = new Set([item.id]); }
function toggleOne(item) { const s = new Set(selectedIds.value); s.has(item.id) ? s.delete(item.id) : s.add(item.id); selectedIds.value = s; }
function rangeSelect(item) {
  const vis = currentItems.value, last = [...selectedIds.value].at(-1);
  if (!last) { selectOne(item); return; }
  const a = vis.findIndex(f => f.id === last), b = vis.findIndex(f => f.id === item.id);
  if (a < 0 || b < 0) { selectOne(item); return; }
  selectedIds.value = new Set(vis.slice(Math.min(a,b), Math.max(a,b)+1).map(f => f.id));
}

// Clipboard
function cutSelection() { clipboard.value = selectedItems.value.map(i => ({ item: i, mode: 'cut' })); toast.add({ severity: 'info', summary: `${clipboard.value.length} cut`, life: 1800 }); }
function copySelection() { clipboard.value = selectedItems.value.map(i => ({ item: i, mode: 'copy' })); toast.add({ severity: 'info', summary: `${clipboard.value.length} copied`, life: 1800 }); }
function pasteClipboard() {
  if (!clipboard.value.length) return;
  emit('copy-paste', { items: clipboard.value.map(c => c.item), targetId: currentFolderId.value, mode: clipboard.value[0].mode });
  if (clipboard.value[0].mode === 'cut') clipboard.value = [];
  toast.add({ severity: 'success', summary: 'Pasted', life: 1800 });
}

// Operations
function renameSelected() { if (selectedIds.value.size !== 1) return; grid.value?.startRenameById([...selectedIds.value][0]); }
function confirmDelete() {
  const count = selectedIds.value.size;
  confirm.require({ message: `Delete ${count} item${count > 1 ? 's' : ''}? This cannot be undone.`, header: 'Confirm Delete', icon: 'bx bx-error-circle', acceptClass: 'p-button-danger',
    accept: () => { emit('delete', selectedItems.value); selectedIds.value = new Set(); toast.add({ severity: 'success', summary: `${count} deleted`, life: 1800 }); },
  });
}
function createFolder() {
  const name = newFolderName.value.trim(); if (!name) return;
  emit('new-folder', name, currentFolderId.value); showNewFolderDialog.value = false; newFolderName.value = '';
  toast.add({ severity: 'success', summary: `"${name}" created`, life: 1800 });
}
function triggerUpload() { uploadInput.value?.click(); }
function onUploadInput(e) { uploadFiles(e.target.files); e.target.value = ''; }
function uploadFiles(fl) { if (fl?.length) emit('upload', fl, currentFolderId.value); }
function formatSize(b) { if (!b) return ''; if (b < 1024) return b+' B'; if (b < 1048576) return (b/1024).toFixed(1)+' KB'; if (b < 1073741824) return (b/1048576).toFixed(1)+' MB'; return (b/1073741824).toFixed(2)+' GB'; }

// Context menu
function showContextMenu({ item, event }) {
  if (!selectedIds.value.has(item.id)) selectOne(item);
  const multi = selectedIds.value.size > 1;
  contextMenuItems.value = [
    { label: 'Open', icon: 'bx bx-folder-open', command: () => openItem(item), visible: !multi },
    { separator: true },
    { label: 'Cut', icon: 'bx bx-cut', command: cutSelection },
    { label: 'Copy', icon: 'bx bx-copy', command: copySelection },
    { label: 'Paste', icon: 'bx bx-paste', command: pasteClipboard, disabled: !clipboard.value.length },
    { separator: true },
    { label: 'Rename', icon: 'bx bx-rename', command: renameSelected, disabled: multi },
    { label: 'Download', icon: 'bx bx-download', command: () => emit('download', selectedItems.value) },
    { separator: true },
    { label: 'Delete', icon: 'bx bx-trash', command: confirmDelete, class: 'fm-ctx-danger' },
  ];
  ctxMenu.value.show(event);
}

// Keyboard
function onKeyDown(e) {
  if (e.key === 'Delete') { if (selectedIds.value.size) confirmDelete(); }
  else if (e.key === 'F2') { renameSelected(); }
  else if (e.ctrlKey && e.key === 'a') { e.preventDefault(); selectedIds.value = new Set(currentItems.value.map(f => f.id)); }
  else if (e.ctrlKey && e.key === 'c') { copySelection(); }
  else if (e.ctrlKey && e.key === 'x') { cutSelection(); }
  else if (e.ctrlKey && e.key === 'v') { pasteClipboard(); }
  else if (e.key === 'Backspace') { goBack(); }
  else if (e.key === 'Escape') { selectedIds.value = new Set(); }
}
</script>

<style>
/* FM tokens mapped directly to app _variables.scss CSS custom properties */
:root, [data-bs-theme] {
  --fm-surface:        var(--bg-panel);
  --fm-text:           var(--text-main);
  --fm-text-secondary: var(--text-muted);
  --fm-text-muted:     var(--text-muted);
  --fm-accent:         var(--primary-color);
  --fm-border:         var(--border-color);
  --fm-item-hover:     var(--item-hover);
  --fm-item-selected:  var(--selection-bg);
  --fm-bar-bg:         var(--bg-app);
  --fm-header-bg:      var(--bg-header);
  --fm-sidebar-bg:     var(--bg-app);
}
</style>

<style scoped>
.fm-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--fm-surface);
  overflow: hidden;
  outline: none;
  color: var(--fm-text);
  font-family: var(--bs-body-font-family, system-ui, sans-serif);
  font-size: 0.875rem;
  border-radius: 10px;
}

/* ── Top bar ─────────────────────────────────────── */
.fm-topbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--fm-border);
  background: var(--fm-header-bg);
  flex-shrink: 0;
  min-height: 42px;
}
.fm-nav { display: flex; gap: 2px; }
.fm-icon-btn {
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  border: none; background: none;
  border-radius: 5px;
  cursor: pointer;
  color: var(--fm-text);
  font-size: 1.1rem;
  transition: background .1s;
}
.fm-icon-btn:hover:not(:disabled) { background: var(--fm-item-hover); }
.fm-icon-btn:disabled { opacity: .35; cursor: default; }
.fm-icon-btn--active { background: var(--fm-item-selected) !important; color: var(--fm-accent); }

.fm-breadcrumb {
  display: flex; align-items: center; gap: 3px;
  flex: 1; min-width: 0; overflow: hidden;
}
.fm-bc-item {
  font-size: 0.8rem; color: var(--fm-text-muted);
  cursor: pointer; padding: 2px 4px; border-radius: 4px;
  display: flex; align-items: center; gap: 3px;
  white-space: nowrap;
  transition: color .1s;
}
.fm-bc-item:hover { color: var(--fm-text); }
.fm-bc-label { font-weight: 500; color: var(--fm-text); }
.fm-bc-sep { font-size: 0.75rem; color: var(--fm-text-muted); flex-shrink: 0; }

.fm-topbar-right { display: flex; align-items: center; gap: 6px; }
.fm-search-wrap {
  display: flex; align-items: center; gap: 6px;
  background: var(--fm-bar-bg);
  border: 1px solid var(--fm-border);
  border-radius: 6px;
  padding: 0 8px;
  color: var(--fm-text-muted);
  font-size: 0.85rem;
}
.fm-search-input {
  background: none; border: none; outline: none;
  color: var(--fm-text); font-size: 0.8rem;
  width: 160px; padding: 4px 0;
}
.fm-view-toggle { display: flex; gap: 1px; }

/* ── Body split ──────────────────────────────────── */
.fm-body { display: flex; flex: 1; min-height: 0; overflow: hidden; }

.fm-sidebar {
  flex-shrink: 0;
  background: var(--fm-sidebar-bg);
  border-right: 1px solid var(--fm-border);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.fm-resize-handle {
  width: 4px; flex-shrink: 0; cursor: col-resize;
  background: var(--fm-border);
  transition: background .15s;
}
.fm-resize-handle:hover { background: var(--fm-accent); }

.fm-content { flex: 1; display: flex; flex-direction: column; min-width: 0; overflow: hidden; }

/* ── Action strip ────────────────────────────────── */
.fm-actionstrip {
  display: flex; align-items: center; gap: 2px;
  padding: 4px 8px;
  border-bottom: 1px solid var(--fm-border);
  background: var(--fm-header-bg);
  flex-shrink: 0;
}
.fm-strip-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 8px; border: none; background: none;
  border-radius: 5px; cursor: pointer;
  font-size: 0.78rem; color: var(--fm-text);
  transition: background .1s;
  white-space: nowrap;
}
.fm-strip-btn:hover:not(:disabled) { background: var(--fm-item-hover); }
.fm-strip-btn:disabled { opacity: .35; cursor: default; }
.fm-strip-btn--danger:hover:not(:disabled) { color: var(--danger-color); background: var(--danger-bg-subtle, rgba(var(--bs-danger-rgb,220,53,69),.08)); }
.fm-strip-sep { width: 1px; height: 18px; background: var(--fm-border); margin: 0 2px; }
.fm-strip-btn--icon { padding: 4px 6px; }
:deep(.fm-sort-sel) {
  font-size: 0.75rem !important;
  height: 28px !important;
  min-width: 130px;
  margin-left: auto;
}
:deep(.fm-sort-sel .p-select-label) { padding: 3px 8px !important; font-size: 0.75rem !important; }
:deep(.fm-sort-sel .p-select-dropdown) { width: 28px !important; }

/* ── Status bar ──────────────────────────────────── */
.fm-statusbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 4px 12px;
  border-top: 1px solid var(--fm-border);
  background: var(--fm-header-bg);
  flex-shrink: 0;
  min-height: 28px;
}
.fm-status-text { font-size: 0.72rem; color: var(--fm-text-muted); }
.fm-storage-inline { display: flex; align-items: center; gap: 8px; }
.fm-storage-track {
  width: 80px; height: 4px; background: var(--fm-bar-bg);
  border-radius: 99px; overflow: hidden;
}
.fm-storage-fill { height: 100%; border-radius: 99px; transition: width .3s; }
.fm-storage-label { font-size: 0.68rem; color: var(--fm-text-muted); }
.fm-storage-label.is-warn { color: var(--danger-color); }
:deep(.fm-ctx-danger) { color: #ef4444; }
</style>